import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import ContentArea, { ContentAreaRef, ContentNodeType } from "@/components/ContentArea";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/context/confirm-context";

interface CommentInputProps {
  onSubmit: (text: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  submitButtonText?: string;
  autoFocus?: boolean;
  showActionsOnMount?: boolean;
  initialValue?: string;
  readOnly?: boolean;
  isSubmitting?: boolean;
}

const CommentInput = ({
  onSubmit,
  onCancel,
  placeholder = "Add a comment...",
  submitButtonText = "Comment",
  autoFocus = false,
  showActionsOnMount = false,
  initialValue = "",
  readOnly = false,
  isSubmitting = false,
}: CommentInputProps) => {
  const [text, setText] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(showActionsOnMount);
  const [resetKey, setResetKey] = useState(0);
  const editorRef = useRef<ContentAreaRef>(null);
  const { confirm } = useConfirm();
  const isConfirmingLeaveRef = useRef(false);

  // Track if there's unsaved comment text
  const hasUnsavedChanges = text.trim().length > 0 && text.trim() !== initialValue.trim();

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if user already confirmed leaving
    if (isConfirmingLeaveRef.current) {
      return false;
    }
    // Don't block same-path navigation
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }
    return hasUnsavedChanges && !isSubmitting;
  });

  // Handle navigation blocker
  useEffect(() => {
    if (blocker.state === "blocked" && !isConfirmingLeaveRef.current) {
      isConfirmingLeaveRef.current = true;
      confirm({
        title: "Unsaved Comment",
        message: "You have an unsaved comment. Are you sure you want to leave?",
        confirmText: "Leave",
        cancelText: "Stay",
        headerVariant: ConfirmationModalVariant.warning,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
      }).then(confirmed => {
        if (confirmed) {
          blocker.proceed();
        } else {
          isConfirmingLeaveRef.current = false;
          blocker.reset();
        }
      });
    }
  }, [blocker, confirm]);

  useEffect(() => {
    if (autoFocus && !readOnly && editorRef.current) {
      editorRef.current.focus();
    }
  }, [autoFocus, readOnly]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  const handleSubmit = () => {
    if (text.trim() && !isSubmitting) {
      onSubmit(text.trim());
      setText(text.trim());
      setResetKey(prev => prev + 1);
      setIsFocused(false);
    }
  };

  const handleCancel = async () => {
    if (isSubmitting) {
      return;
    }

    // If there are unsaved changes, confirm before canceling
    if (hasUnsavedChanges) {
      const confirmed = await confirm({
        title: "Discard Comment?",
        message: "Are you sure you want to discard your comment?",
        confirmText: "Discard",
        cancelText: "Keep Editing",
        headerVariant: ConfirmationModalVariant.warning,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
      });

      if (!confirmed) {
        return;
      }
    }

    setText(initialValue);
    setIsFocused(false);
    setResetKey(prev => prev + 1);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && text.trim() && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape" && !isSubmitting) {
      e.preventDefault();
      handleCancel();
    }
  };

  const showActions = isFocused || text.length > 0 || (!showActionsOnMount && text.length === 0);

  if (readOnly) {
    return (
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <ContentArea
            config={{ disabledNodes: [ContentNodeType.Snippet, ContentNodeType.DatasetExamples] }}
            ref={editorRef}
            key={resetKey}
            enableTagging
            value={text}
            readOnly
            hideContextMenu
            size="xs"
            className="min-h-auto m-0 whitespace-pre-wrap break-words border-0 bg-transparent p-0 text-sm leading-relaxed text-foreground/90"
            containerClassName="border-0 border-none shadow-none p-0"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div className="flex-1">
        <div onKeyDown={handleKeyDown}>
          <ContentArea
            ref={editorRef}
            key={resetKey}
            config={{ disabledNodes: [ContentNodeType.Snippet, ContentNodeType.DatasetExamples] }}
            enableTagging
            value={text}
            readOnly={readOnly || isSubmitting}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            placeholder={placeholder}
            hideContextMenu
            size="xs"
            className={`min-h-[40px] transition-all duration-200 ${
              showActions ? "min-h-[60px]" : ""
            } ${isSubmitting ? "opacity-60" : ""}`}
          />
        </div>
        {showActions && (
          <div className="mt-2 flex items-center justify-between duration-200 animate-in fade-in slide-in-from-top-1">
            <span className="text-xs text-muted-foreground">
              {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"} + Enter to submit · Esc to cancel
            </span>
            <div className="flex gap-2">
              <Button
                onClick={handleCancel}
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-xs"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!text.trim() || isSubmitting}
                size="sm"
                className="h-7 px-3 text-xs"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  submitButtonText
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentInput;
